import React, { useMemo } from 'react';
import { Flex } from 'antd';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker, { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import { DateTime } from 'luxon';
import { useQueryClient } from 'react-query';

import { trackEvent } from 'jf/analytics/Analytics';
import { StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExText } from 'jf/components/DevExText';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

import { CloseStudyModal } from '../CloseStudyModal';

export const ManageCloseDateCard: React.FC<{ studyRef: string }> = (props) => {
  const theme = useDevExTheme();
  const [isEditing, setIsEditing] = React.useState(false);

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef: props.studyRef });
  const { mutateAsync: updateStudy, isLoading: isUpdating } = useClientMutation(
    StudyClient.updateStudy
  );
  const queryClient = useQueryClient();
  const [newCloseDate, setNewCloseDate] = React.useState(
    study?.scheduledCloseDate || dayjs().add(2, 'week').startOf('day').toISOString()
  );
  const closeStudyModal = useJFOverlay(CloseStudyModal);

  const onSave = () => {
    updateStudy({
      studyRef: props.studyRef,
      requestBody: {
        scheduledCloseDate: newCloseDate,
      },
    }).then(() => {
      queryClient.invalidateQueries(['GET_STUDY', props.studyRef]);
      queryClient.invalidateQueries('GET_STUDIES');
    });
    setIsEditing(false);
  };
  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // Can not select days before or on the current date, or weekends (Saturday/Sunday)
    return (
      current && (current < dayjs().endOf('day') || current.day() === 0 || current.day() === 6)
    );
  };
  const allPromptsAreOpenEnd = useMemo(() => {
    return study?.prompts.every((prompt) => prompt.type === 'OPEN_END') || false;
  }, [study]);
  const onCloseStudy = () => {
    trackEvent('survey-analyzer:close:start', { surveyRef: props.studyRef });
    closeStudyModal.open({ autorunHighlights: !allPromptsAreOpenEnd });
  };
  return (
    <DevExCard
      title={
        <DevExText fontSize="md" strong>
          Survey Close Date
        </DevExText>
      }
      extra={
        <Flex gap={theme.variable.spacing.sm}>
          {isEditing ? (
            <>
              <DevExButton
                onClick={() => {
                  setIsEditing(false);
                }}
              >
                Cancel
              </DevExButton>
              <DevExButton type="primary" disabled={isUpdating} onClick={onSave}>
                Save
              </DevExButton>
            </>
          ) : (
            <>
              <DevExButton onClick={onCloseStudy} type="secondary">
                Close now
              </DevExButton>
              <DevExButton
                icon={<FontAwesomeIcon icon={faPen} />}
                onClick={() => setIsEditing(true)}
                type="outline"
              />
            </>
          )}
        </Flex>
      }
    >
      {isEditing ? (
        <Flex align="center" gap={theme.variable.spacing.sm}>
          <DevExText>This survey will close automatically</DevExText>
          <DatePicker
            disabledDate={disabledDate}
            value={dayjs(newCloseDate)}
            onChange={(date) => {
              setNewCloseDate(date.toISOString());
            }}
          />
        </Flex>
      ) : !study?.scheduledCloseDate ? (
        <DevExText>No close date configured</DevExText>
      ) : (
        <DevExText lined>
          The survey will close automatically on{' '}
          <DevExText strong>
            {DateTime.fromISO(study?.scheduledCloseDate).toFormat('cccc, MMM d, yyyy')} at 9am EST
          </DevExText>{' '}
          unless you close it before you extend the deadline.
        </DevExText>
      )}
    </DevExCard>
  );
};
